/**
 * @fileoverview Changing the signed-in org identity's own password: an
 * administrator's (Settings → Admins) or a program viewer's (Profile).
 *
 * `POST /org/auth/change-password` checks the current password, ends every
 * other session the account holds and keeps this one signed in. A controlled
 * form: eight characters at least, typed twice.
 */

import { useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button } from "@/components/studio/primitives";

const field =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[11px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";
const label = "mb-1.5 block font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]";

export default function OrgChangePassword({ className = "flex max-w-sm flex-col gap-4" }) {
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [changing, setChanging] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pw.next !== pw.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setChanging(true);
    try {
      await orgApi.post("/org/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't change password"));
    } finally {
      setChanging(false);
    }
  };

  return (
    <form onSubmit={submit} className={className}>
      <div>
        <label className={label} htmlFor="org-pw-current">Current password</label>
        <input
          id="org-pw-current"
          type="password"
          value={pw.current}
          onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
          data-testid="org-pw-current"
          className={field}
          autoComplete="current-password"
        />
      </div>
      <div>
        <label className={label} htmlFor="org-pw-new">New password</label>
        <input
          id="org-pw-new"
          type="password"
          value={pw.next}
          onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
          data-testid="org-pw-new"
          className={field}
          autoComplete="new-password"
        />
      </div>
      <div>
        <label className={label} htmlFor="org-pw-confirm">Confirm new password</label>
        <input
          id="org-pw-confirm"
          type="password"
          value={pw.confirm}
          onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
          data-testid="org-pw-confirm"
          className={field}
          autoComplete="new-password"
        />
      </div>
      <div>
        <Button type="submit" disabled={changing} icon={changing ? "loader" : "check"} data-testid="org-pw-submit">
          {changing ? "Saving…" : "Change password"}
        </Button>
      </div>
    </form>
  );
}
